import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Check, Loader2, Pencil, Sparkles, X } from "lucide-react";
import {
  DRAFTED_BY_COPILOT_LEAD,
  assumedDisclosure,
  confirmTextForAction,
  fieldInputType,
  formatFieldValue,
} from "../../helpers/copilotResult";
import "./CopilotResultCard.scss";

const FieldRow = ({ field, editing, onBeginEdit, onCancel, onSave, saving, error }) => {
  const [value, setValue] = useState("");

  const beginEdit = () => {
    setValue(field.value === null || field.value === undefined ? "" : `${field.value}`);
    onBeginEdit(field.key);
  };

  const onKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      onSave(field.key, value);
    }
    if (event.key === "Escape") {
      event.stopPropagation();
      onCancel();
    }
  };

  return (
    <div className="copilot-result__field">
      <dt className="copilot-result__field-label">
        {field.label}
        {field.assumed && (
          <span className="copilot-result__assumed">Assumed</span>
        )}
      </dt>
      <dd className="copilot-result__field-value">
        {editing ? (
          <span className="copilot-result__edit">
            <input
              type={fieldInputType(field)}
              value={value}
              onChange={(event) => setValue(event.target.value)}
              onKeyDown={onKeyDown}
              aria-label={`${field.label} value`}
              autoFocus
            />
            <button
              type="button"
              className="copilot-result__edit-save"
              onClick={() => onSave(field.key, value)}
              disabled={saving}
              aria-label={`Save ${field.label}`}
            >
              {saving ? (
                <Loader2 size={14} className="copilot-result__spin" aria-hidden="true" />
              ) : (
                <Check size={14} aria-hidden="true" />
              )}
            </button>
            <button
              type="button"
              className="copilot-result__edit-cancel"
              onClick={onCancel}
              disabled={saving}
              aria-label={`Cancel editing ${field.label}`}
            >
              <X size={14} aria-hidden="true" />
            </button>
          </span>
        ) : field.editable ? (
          <button
            type="button"
            className="copilot-result__field-edit"
            onClick={beginEdit}
            aria-label={`Edit ${field.label}`}
          >
            <span>{formatFieldValue(field)}</span>
            <Pencil size={12} aria-hidden="true" />
          </button>
        ) : (
          <span>{formatFieldValue(field)}</span>
        )}
        {error && <span className="copilot-result__field-error">{error}</span>}
      </dd>
    </div>
  );
};

const CopilotResultCard = ({ result, onEditField, onAction, openInPlace, disableLinks }) => {
  const [editingKey, setEditingKey] = useState(null);
  const [savingKey, setSavingKey] = useState(null);
  const [fieldError, setFieldError] = useState(null);
  const [confirming, setConfirming] = useState(null);
  const [runningAction, setRunningAction] = useState(null);

  if (!result) return null;

  const disclosure = assumedDisclosure(result);

  const saveField = async (key, value) => {
    setSavingKey(key);
    setFieldError(null);
    try {
      await onEditField(key, value);
      setEditingKey(null);
    } catch (err) {
      setFieldError({
        key,
        message: err?.message || "That did not save. Try again.",
      });
    } finally {
      setSavingKey(null);
    }
  };

  const runAction = async (action) => {
    setConfirming(null);
    setRunningAction(action.kind);
    try {
      // NOTE: there is no scoped, typed backend endpoint yet for
      // delete/publish/archive/syndicate actions - onAction re-enters the
      // chat/agent loop with a free-text prompt built from the action label
      // and result title, and trusts the model to carry it out correctly.
      // The confirmation dialog above no longer trusts model-authored copy
      // (see confirmTextForAction), but clicking "confirm" still isn't
      // bound to a scoped mutation the way a real destructive-action
      // confirmation normally would be. A typed per-action endpoint is a
      // real follow-up (flagged in review) - out of scope for this fix.
      await onAction(action);
    } finally {
      setRunningAction(null);
    }
  };

  return (
    <section className="copilot-result" aria-label={result.title}>
      <header className="copilot-result__header">
        <h4 className="copilot-result__title">{result.title}</h4>
        {result.status && (
          <span className="copilot-result__status">{result.status}</span>
        )}
      </header>

      {disclosure && (
        <div className="copilot-result__disclosure">
          <Sparkles
            size={15}
            className="copilot-result__disclosure-icon"
            aria-hidden="true"
          />
          <p className="copilot-result__disclosure-text">
            <span className="copilot-result__disclosure-lead">
              {DRAFTED_BY_COPILOT_LEAD}
            </span>{" "}
            {disclosure}
          </p>
        </div>
      )}

      <dl className="copilot-result__fields">
        {result.fields.map((field) => (
          <FieldRow
            key={field.key}
            field={field}
            editing={editingKey === field.key}
            saving={savingKey === field.key}
            error={fieldError?.key === field.key ? fieldError.message : null}
            onBeginEdit={setEditingKey}
            onCancel={() => {
              setEditingKey(null);
              setFieldError(null);
            }}
            onSave={saveField}
          />
        ))}
      </dl>

      {confirming && (
        <div className="copilot-result__confirm" role="alertdialog" aria-label={confirming.label}>
          <p>{confirmTextForAction(confirming, result)}</p>
          <div className="copilot-result__confirm-actions">
            <button
              type="button"
              className="copilot-result__action copilot-result__action--primary"
              onClick={() => runAction(confirming)}
            >
              {confirming.label}
            </button>
            <button
              type="button"
              className="copilot-result__action"
              onClick={() => setConfirming(null)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {result.actions.length > 0 && !confirming && (
        <div className="copilot-result__actions">
          {result.actions.map((action, index) => {
            // Keyed on index+label, not action.kind alone - two actions in
            // the same result can share a kind (nothing in
            // normalizeAction/normalizeResult de-dupes on it), which
            // collided on this React key and made them share
            // disabled/running state across each other (a bug caught in
            // review). No stable id field exists on the action payload
            // shape (see copilotResult.js's normalizeAction), so index+label
            // is the fallback.
            const actionKey = `${index}:${action.label}`;
            return action.href ? (
              // openInPlace/disableLinks mirror ListingCard's: in place only
              // on the full chat page (the docked panel keeps target="_blank"
              // so it doesn't navigate away from whatever the agent opened it
              // over), and disabled while a turn is streaming so following a
              // link can't unmount the panel mid-reply and abort it (see the
              // unmount-abort effect below in HelpChatPanel).
              disableLinks && openInPlace ? (
                <span
                  key={actionKey}
                  className={`copilot-result__action copilot-result__action--disabled${
                    action.primary ? " copilot-result__action--primary" : ""
                  }`}
                  aria-disabled="true"
                >
                  {action.label}
                </span>
              ) : (
                <Link
                  key={actionKey}
                  to={action.href}
                  target={openInPlace ? undefined : "_blank"}
                  rel={openInPlace ? undefined : "noopener noreferrer"}
                  className={`copilot-result__action${
                    action.primary ? " copilot-result__action--primary" : ""
                  }`}
                >
                  {action.label}
                </Link>
              )
            ) : (
              <button
                key={actionKey}
                type="button"
                className="copilot-result__action copilot-result__action--primary"
                onClick={() => setConfirming(action)}
                disabled={runningAction === action.kind}
              >
                {runningAction === action.kind ? "Working..." : action.label}
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default CopilotResultCard;
